import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BankOutlined,
  BarChartOutlined,
  BookOutlined,
  CalendarOutlined,
  CommentOutlined,
  CustomerServiceOutlined,
  FileTextOutlined,
  HomeOutlined,
  LayoutOutlined,
  LeftOutlined,
  LogoutOutlined,
  RightOutlined,
  RobotOutlined,
  SearchOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { HOTELSRIVER_OFFICIAL_LOGO_URL } from "../../utils/hotelsRiverBrandLogo";
import { useAppContext } from "../../contexts/AppContext";
import { PACKAGES, useSearch } from "../../contexts/SearchContext";
import {
  getUserDetails,
  isAdminUser,
  logout,
  resolveSessionAgentId,
} from "../../services/authService";
import { getUserInfo } from "../../services/localStorage";
import { formatFxRateDisplay } from "../../Components/CurrencyRatesBar/CurrencyRatesBar";
import { useRemindersHub } from "../reminders/context/RemindersHubContext";
import { navigateAgentPackageUpgrade } from "../../utils/agentUpgradeNavigation";
import { fetchAgentPublicLeads } from "../../services/publicSiteLeadsApi";
import { countNewCrmProspectLeads } from "../../Pages/Agent/Crm/crmLeadFormatters";
import { useAgentEntitlements } from "../../features/agentEntitlements/useAgentEntitlements";
import { useCrmHubEnabled } from "../../utils/useCrmHubEnabled";
import { buildCrmOpsDocketPath } from "../../Pages/Agent/Crm/navigateToCrmOpsDocket";
import {
  normalizeAgentImageValue,
  resolveAgentCompanyLogoUrl,
} from "../../utils/resolveAgentCompanyLogoUrl";
import "./AgentPackagesResultsChrome.css";

const NAV_TABS = [
  { id: "search", label: "חיפוש חבילות", path: "/p/dashboard/searchoffer" },
  { id: "orders", label: "הזמנות", path: "/p/dashboard/orders" },
  { id: "customers", label: "לקוחות", path: "/p/dashboard/customers" },
];

/** Primary rail icons (mock parity) — always visible. */
const RAIL_PRIMARY = [
  { id: "search", icon: SearchOutlined, label: "חיפוש", path: "/p/dashboard/searchoffer" },
  { id: "home", icon: CalendarOutlined, label: "יומן / בית", path: "/p/dashboard/home" },
  { id: "docs", icon: FileTextOutlined, label: "CRM / מסמכים", path: "/p/dashboard/drafts" },
  { id: "finance", icon: BarChartOutlined, label: "פיננסים", path: "/p/dashboard/finance" },
  { id: "settings", icon: SettingOutlined, label: "הגדרות", path: "/p/dashboard/management" },
];

/** Extra destinations from legacy SideNav — shown when rail is expanded. */
const RAIL_EXTRA = [
  { id: "home-extra", icon: HomeOutlined, label: "דף הבית", path: "/p/dashboard/home" },
  { id: "academy", icon: BookOutlined, label: "אקדמיה", path: "/p/dashboard/academy" },
  { id: "mano", icon: BankOutlined, label: "הצעות מנו", path: "/p/dashboard/mano-quotes" },
  {
    id: "site-settings",
    icon: LayoutOutlined,
    label: "הגדרות האתר שלי",
    path: "/p/dashboard/site-settings",
  },
  {
    id: "customer-chat",
    icon: CustomerServiceOutlined,
    label: "צ'אט לקוחות",
    path: "/p/dashboard/customer-chat-inbox",
  },
];

function resolveActiveNavTab(pathname) {
  if (pathname.includes("/orders")) return "orders";
  if (pathname.includes("packages-success-mockup")) return "orders";
  if (pathname.includes("/customers")) return "customers";
  if (pathname.includes("/searchoffer")) return "search";
  return null;
}

function resolveActiveRail(pathname) {
  const p = pathname || "";
  if (p.includes("/finance")) return "finance";
  if (p.includes("/drafts") || p.includes("/sales-links") || p.includes("/ai-sales-leads")) {
    return "docs";
  }
  if (p.includes("/management") || p.includes("/site-settings")) return "settings";
  if (p.includes("/home")) return "home";
  if (p.includes("/academy")) return "academy";
  if (p.includes("/mano-quotes")) return "mano";
  if (p.includes("/customer-chat")) return "customer-chat";
  if (p.includes("/admin/operation-requests")) return "ops-admin";
  if (p.includes("/admin")) return "admin";
  if (p.includes("/searchoffer") || p.includes("/orders") || p.includes("/customers")) {
    return "search";
  }
  return null;
}

function dashboardPath(pathname) {
  return (
    pathname.includes("/p/dashboard") ||
    /\/dashboard(\/|$)/.test(pathname)
  );
}

/** New header + icon rail (replaces UpperNavBarAgent + SideNav). */
export default function AgentPackagesResultsChrome({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currencyRate } = useAppContext();
  const { switchFromOrganizedToursIfNeeded } = useSearch();
  const { summary } = useRemindersHub();
  const {
    canUseLeadsSystem,
    canUseAgentPublicSite,
    canUseManoQuotes,
    status: entitlementsStatus,
  } = useAgentEntitlements();
  const crmHubEnabled = useCrmHubEnabled();
  const user = getUserDetails();
  const isAdmin = isAdminUser();
  const [newLeadsCount, setNewLeadsCount] = useState(0);
  const [railExpanded, setRailExpanded] = useState(false);

  const activeNav = resolveActiveNavTab(location.pathname || "");
  const activeRail = resolveActiveRail(location.pathname || "");

  const displayName = useMemo(() => {
    const u = user && typeof user === "object" ? user : {};
    const first = String(u.first_name || u.firstName || "").trim();
    const last = String(u.last_name || u.lastName || "").trim();
    const full = `${first} ${last}`.trim();
    if (full) return full;
    return String(u.name || u.email || "סוכן").trim() || "סוכן";
  }, [user]);

  const displayEmail = useMemo(() => {
    const u = user && typeof user === "object" ? user : {};
    return String(u.email || "").trim();
  }, [user]);

  const profileSrc = useMemo(() => {
    try {
      const info = getUserInfo();
      const raw =
        info?.agent_image || info?.agentImage || user?.agent_image || user?.agentImage || null;
      const normalized = normalizeAgentImageValue(raw);
      if (normalized) return normalized;
      return resolveAgentCompanyLogoUrl(info || user) || null;
    } catch {
      return null;
    }
  }, [user]);

  const logoSrc = useMemo(() => {
    try {
      return resolveAgentCompanyLogoUrl(getUserInfo() || user) || HOTELSRIVER_OFFICIAL_LOGO_URL;
    } catch {
      return HOTELSRIVER_OFFICIAL_LOGO_URL;
    }
  }, [user]);

  const fxItems = useMemo(() => {
    const order = [
      { key: "USD", className: "hrm-fx__item--usd", symbol: "$", title: "דולר" },
      { key: "EUR", className: "hrm-fx__item--eur", symbol: "€", title: "יורו" },
      { key: "GBP", className: "hrm-fx__item--gbp", symbol: "£", title: "פאונד" },
    ];
    return order
      .map((item) => {
        const formatted = formatFxRateDisplay(currencyRate?.[item.key]);
        if (!formatted) return null;
        return { ...item, formatted };
      })
      .filter(Boolean);
  }, [currencyRate]);

  const hasAgentSession = Boolean(resolveSessionAgentId());

  const railExtraItems = useMemo(() => {
    const items = [...RAIL_EXTRA];
    if (canUseLeadsSystem) {
      items.splice(2, 0, {
        id: "ai-leads",
        icon: RobotOutlined,
        label: "לידים AI",
        path: crmHubEnabled ? "/p/dashboard/drafts?tab=leads" : "/p/dashboard/ai-sales-leads",
      });
    }
    if (isAdmin) {
      items.push(
        {
          id: "ops-admin",
          icon: CommentOutlined,
          label: "פניות אופרציה",
          path: "/p/dashboard/admin/operation-requests",
        },
        {
          id: "admin",
          icon: SettingOutlined,
          label: "ניהול מערכת",
          path: "/p/dashboard/admin",
        }
      );
    }
    return items;
  }, [canUseLeadsSystem, crmHubEnabled, isAdmin]);

  useEffect(() => {
    if (entitlementsStatus !== "ok" || !canUseLeadsSystem) {
      setNewLeadsCount(0);
      return undefined;
    }
    let cancelled = false;
    // Same contract as UpperNavBarAgent: res.leads array + admin scope=all for Meta pool.
    const fetchNewLeadsCount = async () => {
      try {
        const res = await fetchAgentPublicLeads({
          limit: 100,
          scope: isAdmin ? "all" : undefined,
        });
        if (!res?.success) {
          if (!cancelled) setNewLeadsCount(0);
          return;
        }
        const leads = Array.isArray(res?.leads) ? res.leads : [];
        const next = countNewCrmProspectLeads(leads);
        if (!cancelled) {
          setNewLeadsCount(
            Number.isFinite(next) && next > 0 ? Math.floor(next) : 0
          );
        }
      } catch {
        if (!cancelled) setNewLeadsCount(0);
      }
    };
    fetchNewLeadsCount();
    const interval = setInterval(fetchNewLeadsCount, 60000);
    const refreshHandler = () => fetchNewLeadsCount();
    window.addEventListener("publicLeadsStatusChanged", refreshHandler);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("publicLeadsStatusChanged", refreshHandler);
    };
  }, [canUseLeadsSystem, entitlementsStatus, isAdmin]);

  const operationUnread = Number(summary?.operation_unread) || 0;
  const operationUnreadAgent = Number(summary?.operation_unread_agent) || 0;
  const operationUnreadAdmin = Number(summary?.operation_unread_admin) || 0;
  const operationFirstUnread = summary?.operation_first_unread || null;
  const hasOperationUnread = operationUnread > 0;
  const hasOperationFirstUnread = Boolean(
    operationFirstUnread &&
      (operationFirstUnread.id || operationFirstUnread.orderId || operationFirstUnread.order_id)
  );
  const preferAgentOpsBell =
    operationUnreadAgent > 0 || hasOperationFirstUnread || (!isAdmin && hasOperationUnread);
  const messagesCount = hasOperationUnread
    ? preferAgentOpsBell
      ? operationUnreadAgent || operationUnread
      : isAdmin
        ? operationUnreadAdmin || operationUnread
        : operationUnread
    : 0;

  const goSearch = () => {
    switchFromOrganizedToursIfNeeded(PACKAGES);
    navigate("/p/dashboard/searchoffer");
  };

  const logoutAgent = () => {
    logout();
    window.location.href = "/login";
  };

  const navigateRail = (item) => {
    if (!item?.path) return;
    if (item.id === "site-settings" && !canUseAgentPublicSite) {
      navigateAgentPackageUpgrade(navigate, "side_nav_site_settings_locked");
      return;
    }
    if (item.id === "mano" && !canUseManoQuotes) {
      navigateAgentPackageUpgrade(navigate, "side_nav_mano_quotes_locked");
      return;
    }
    if (item.id === "search") {
      goSearch();
      return;
    }
    navigate(item.path);
  };

  const navigateToLeads = () => {
    if (crmHubEnabled) {
      navigate("/p/dashboard/drafts?tab=leads");
      return;
    }
    navigate("/p/dashboard/ai-sales-leads");
  };

  const navigateToAgentOpsChat = () => {
    const first = operationFirstUnread;
    navigate(
      buildCrmOpsDocketPath({
        orderId: String(first?.orderId || first?.order_id || "").trim() || undefined,
        opsType: String(first?.requestType || first?.request_type || "").trim() || undefined,
        requestId: String(first?.id || "").trim() || undefined,
      })
    );
  };

  const navigateToMessages = () => {
    if (hasOperationUnread) {
      if (preferAgentOpsBell || !isAdmin) {
        navigateToAgentOpsChat();
        return;
      }
      navigate("/p/dashboard/admin/operation-requests");
      return;
    }
    if (hasAgentSession) {
      navigateToAgentOpsChat();
      return;
    }
    if (isAdmin) {
      navigate("/p/dashboard/admin/operation-requests");
    }
  };

  const leadsChipLabel = newLeadsCount > 0 ? `${newLeadsCount} לידים חדשים` : "0 לידים";
  const messagesChipLabel = `${messagesCount} הודעות`;

  const renderRailButton = (item) => {
    const Icon = item.icon;
    const active = item.id === activeRail;
    return (
      <button
        key={item.id}
        type="button"
        className={`hrm-rail__btn${active ? " hrm-rail__btn--active" : ""}${
          railExpanded ? " hrm-rail__btn--wide" : ""
        }`}
        title={item.label}
        aria-label={item.label}
        onClick={() => navigateRail(item)}
      >
        <Icon />
        {railExpanded ? <span className="hrm-rail__label">{item.label}</span> : null}
      </button>
    );
  };

  return (
    <div
      className={`hrm-live${railExpanded ? " hrm-live--rail-expanded" : ""}`}
      dir="rtl"
      lang="he"
    >
      <aside className={`hrm-rail${railExpanded ? " hrm-rail--expanded" : ""}`} aria-label="ניווט מהיר">
        <button
          type="button"
          className="hrm-rail__toggle"
          aria-label={railExpanded ? "כווץ תפריט צד" : "הרחב תפריט צד"}
          aria-expanded={railExpanded}
          title={railExpanded ? "כווץ" : "כל הכלים"}
          onClick={() => setRailExpanded((v) => !v)}
        >
          {railExpanded ? <RightOutlined /> : <LeftOutlined />}
        </button>

        <div className="hrm-rail__group" role="navigation" aria-label="ניווט ראשי">
          {RAIL_PRIMARY.map(renderRailButton)}
        </div>

        {railExpanded ? (
          <div className="hrm-rail__group hrm-rail__group--extra" role="navigation" aria-label="כלים נוספים">
            <p className="hrm-rail__group-title">עוד כלים</p>
            {railExtraItems.map(renderRailButton)}
          </div>
        ) : null}
      </aside>

      <div className="hrm-main-shell">
        <header className="hrm-topbar">
          <div className="hrm-topbar__start">
            <div className="hrm-topbar__brand">
              <button
                type="button"
                className="hrm-topbar__logo-btn"
                onClick={() => navigate("/p/dashboard/home")}
                aria-label="דף הבית"
              >
                <img src={logoSrc} alt="HotelsRiver" className="hrm-topbar__logo" />
              </button>
            </div>
            <nav className="hrm-topbar__tabs" aria-label="ניווט ראשי">
              {NAV_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`hrm-tab${activeNav === tab.id ? " hrm-tab--active" : ""}`}
                  onClick={() => {
                    if (tab.id === "search") {
                      goSearch();
                      return;
                    }
                    navigate(tab.path);
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="hrm-topbar__end" aria-label="כלי סוכן">
            {fxItems.length > 0 ? (
              <div className="hrm-fx" role="group" aria-label="שערי מטבע">
                {fxItems.map((item) => (
                  <span key={item.key} className={`hrm-fx__item ${item.className}`}>
                    <abbr title={item.title}>{item.symbol}</abbr>
                    {item.formatted}
                  </span>
                ))}
              </div>
            ) : null}

            {fxItems.length > 0 ? <div className="hrm-topbar__divider" aria-hidden /> : null}

            {canUseLeadsSystem ? (
              <button
                type="button"
                className={`hrm-chip hrm-chip--leads${
                  newLeadsCount === 0 ? " hrm-chip--leads-zero" : ""
                }`}
                onClick={navigateToLeads}
                aria-label={leadsChipLabel}
                title={leadsChipLabel}
              >
                <span className="hrm-chip__n">
                  {newLeadsCount > 99 ? "99+" : newLeadsCount}
                </span>
                <span className="hrm-chip__t">לידים</span>
              </button>
            ) : null}

            <button
              type="button"
              className="hrm-chip hrm-chip--msgs"
              onClick={navigateToMessages}
              aria-label={messagesChipLabel}
              title={messagesChipLabel}
            >
              <span className="hrm-chip__n">{messagesCount}</span>
              <span className="hrm-chip__t">הודעות</span>
            </button>

            <button
              type="button"
              className="hrm-topbar__user"
              onClick={() => navigate("/p/dashboard/management")}
            >
              <span className="hrm-topbar__user-text">
                <strong>{displayEmail || displayName}</strong>
                <span>{displayEmail ? displayName : "סוכן נסיעות"}</span>
              </span>
              <span className="hrm-avatar" aria-hidden>
                {profileSrc ? <img src={profileSrc} alt="" /> : <UserOutlined />}
              </span>
            </button>

            <button
              type="button"
              className="hrm-back-btn"
              aria-label="יציאה"
              title="יציאה"
              onClick={logoutAgent}
            >
              <LogoutOutlined />
            </button>

            <button
              type="button"
              className="hrm-back-btn"
              aria-label="חזרה"
              onClick={() => {
                if (window.history.length > 1) navigate(-1);
                else navigate("/p/dashboard/home");
              }}
            >
              <LeftOutlined />
            </button>
          </div>
        </header>

        <div className="hrm-body">{children}</div>
      </div>
    </div>
  );
}

/**
 * App-wide agent chrome: dark rail + white topbar on all Hebrew dashboard pages,
 * including payment. Hotel-partner mockups and results mockups keep their own chrome.
 */
export function useIsPackagesResultsChromeActive() {
  const location = useLocation();
  const path = location.pathname || "";
  if (!dashboardPath(path)) return false;

  if (
    path.includes("packages-results-mockup") ||
    path.includes("packages-payment-mockup") ||
    path.includes("hotels-results-mockup") ||
    path.includes("hotel-profile-mockup") ||
    path.includes("hotels-search-mockup") ||
    path.includes("unified-search-mockup") ||
    path.includes("booking-confirmation-mockup") ||
    path.includes("hotel-partner-portal-mockup") ||
    path.includes("hotel-partner-admin-mockup")
  ) {
    return false;
  }

  if (/\/p\/dashboard\/dynamic\/profile\/[^/]+\/?$/.test(path)) {
    return false;
  }

  return true;
}
